"use client";

import Image from "next/image";
import Link from "next/link";
import type { SVGProps } from "react";
import { Globe, X } from "lucide-react"; // Import Lucide icons

// Brand icons were removed from lucide-react 1.x; these keep the original lucide 0.468 shapes (ISC license)
const brandIconProps: SVGProps<SVGSVGElement> = {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
};

const BRAND_ICONS = {
    linkedin: (
        <>
            <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
            <rect width="4" height="12" x="2" y="9" />
            <circle cx="4" cy="4" r="2" />
        </>
    ),
    github: (
        <>
            <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />
            <path d="M9 18c-4.51 2-5-2-7-2" />
        </>
    ),
    facebook: <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />,
};

function BrandIcon({ name, className }: { name: keyof typeof BRAND_ICONS; className?: string }) {
    return (
        <svg {...brandIconProps} className={className}>
            {BRAND_ICONS[name]}
        </svg>
    );
}
import { buildLocalizedPath, type Language } from "@/lib/i18n";
import { useTranslations } from "@/components/i18n/use-translations";
import { elktrail, webioc } from "@/data/discoverability";

// X, LinkedIn, Facebook and GitHub profiles are hidden for now; flip to true to show them again
const SHOW_SOCIAL_PROFILES = false;

export default function Footer({ lang }: { lang: Language }) {
    const { messages } = useTranslations();

    const links = [
        { href: buildLocalizedPath("/terms", lang), label: messages.footer.terms },
        { href: buildLocalizedPath("/webioc/privacy", lang), label: messages.footer.webiocPrivacy },
        { href: buildLocalizedPath("/elktrail/privacy", lang), label: messages.footer.elktrailPrivacy },
        { href: buildLocalizedPath("/submit-feed", lang), label: messages.footer.submitFeed },
        { href: buildLocalizedPath("/faq", lang), label: messages.footer.faq },
    ];

    return (
        <footer className="mt-auto w-full border-t border-slate-800/60 bg-linear-to-r from-slate-950 via-slate-900 to-slate-950 text-gray-100">
            {/* One row on desktop; wraps into centered lines on narrow screens */}
            <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-center gap-x-6 gap-y-3 px-4 py-4 md:h-[52px] md:flex-nowrap md:justify-start md:py-0 md:px-6">
                <div className="flex shrink-0 items-center gap-3">
                    <Link href={buildLocalizedPath("/", lang)} className="flex items-center gap-2" prefetch={false} aria-label="ScaMatrix home">
                        <Image src="/icon.svg" alt="" width={20} height={20} className="h-5 w-5" />
                        <span className="title-scamatrix text-sm font-semibold text-gray-100">
                            Sca<span className="bg-linear-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">Matrix</span>
                        </span>
                    </Link>
                    <span className="whitespace-nowrap text-xs text-gray-500">
                        &copy; {new Date().getFullYear()} &middot; {messages.footer.rights}
                    </span>
                </div>

                <nav aria-label={messages.footer.legal} className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 md:ms-auto md:flex-nowrap">
                    {links.map((item) => (
                        <Link key={item.href} href={item.href} className="whitespace-nowrap text-[13px] text-gray-500 transition-colors hover:text-gray-200">
                            {item.label}
                        </Link>
                    ))}
                </nav>

                <span className="hidden h-[18px] w-px bg-slate-700/70 md:block" aria-hidden="true" />

                <div className="flex shrink-0 items-center gap-3.5">
                    {SHOW_SOCIAL_PROFILES && (
                        <>
                            <Link href="https://x.com/ScaMatrix" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-cyan-400 transition-colors" aria-label="Connect on X">
                                <X className="h-4 w-4" />
                            </Link>
                            <Link href="https://www.linkedin.com/company/scamatrix/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-cyan-400 transition-colors" aria-label="Connect on LinkedIn">
                                <BrandIcon name="linkedin" className="h-4 w-4" />
                            </Link>
                            <Link href="https://www.facebook.com/scamatrix" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-cyan-400 transition-colors" aria-label="Connect on Facebook">
                                <BrandIcon name="facebook" className="h-4 w-4" />
                            </Link>
                            <Link href="https://github.com/clizsec" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-cyan-400 transition-colors" aria-label="My GitHub Profile">
                                <BrandIcon name="github" className="h-4 w-4" />
                            </Link>
                        </>
                    )}
                    <Link href="https://www.clizsec.com/en" target="_blank" rel="noopener noreferrer me" className="text-gray-500 hover:text-cyan-400 transition-colors" aria-label="clizSec portfolio" title="clizSec">
                        <Globe className="h-4 w-4" />
                    </Link>
                    <Link href={webioc.chromeWebStoreUrl} target="_blank" rel="noopener noreferrer" className="opacity-85 transition-opacity hover:opacity-100" aria-label="WebIOC Chrome extension" title="WebIOC">
                        <Image src="/images/products/webioc.svg" alt="" width={18} height={18} className="h-[18px] w-[18px]" />
                    </Link>
                    <Link href={elktrail.chromeWebStoreUrl} target="_blank" rel="noopener noreferrer" className="opacity-85 transition-opacity hover:opacity-100" aria-label="ELKTrail Chrome extension" title="ELKTrail">
                        <Image src="/images/products/elktrail.svg" alt="" width={18} height={18} className="h-[18px] w-[18px]" />
                    </Link>
                </div>
            </div>
        </footer>
    );
}
